import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, MapPin, Sparkles, Wand2 } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import LeafletPinPicker from '../../../components/LeafletPinPicker';
import {
  UIButton,
  SectionContainer,
  Stepper,
  FormSection,
  FormField,
  InlineStatusRow,
  ReviewCard,
  StickyActionBar,
  LocationFieldGroup,
  MapReviewCard,
} from '../../../features/ui-system';

const STEPS = [
  { id: 1, title: 'Data Pelanggan', desc: 'Identitas, paket, dan lokasi instalasi' },
  { id: 2, title: 'Infrastruktur', desc: 'Site -> NAS -> OLT -> ODP' },
  { id: 3, title: 'Review', desc: 'Konfirmasi sebelum simpan' },
];

const INIT = {
  customerName: '', phone: '', email: '', address: '', packageId: '', notes: '',
  accessType: 'FTTH_PPPOE', pppoeUsername: '', pppoePassword: '', portalPassword: '',
  latitude: '', longitude: '', siteId: '', nasId: '', oltId: '', odpId: '',
  ponPort: '', onuSerial: '', genieacsDeviceId: '',
};

const DEFAULT_CUSTOMER_MAP_CENTER = { latitude: -3.3869, longitude: 119.3098 };
const id = (v) => String(v || '').trim();
const money = (v) => `Rp ${Number(v || 0).toLocaleString('id-ID')}`;
const inputClassName = 'border-0 bg-[rgba(10,17,28,0.82)] text-[#d5e4f4] focus-visible:ring-2 focus-visible:ring-emerald-300/35';

const hasValidCoordinates = (lat, lon) => {
  const latitude = Number(lat);
  const longitude = Number(lon);
  return Number.isFinite(latitude) && Number.isFinite(longitude) && latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180;
};

const toRadians = (value) => (Number(value) * Math.PI) / 180;
const computeDistanceMeters = (aLat, aLon, bLat, bLon) => {
  const lat1 = Number(aLat); const lon1 = Number(aLon); const lat2 = Number(bLat); const lon2 = Number(bLon);
  if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) return Number.POSITIVE_INFINITY;
  const earthRadius = 6371000;
  const dLat = toRadians(lat2 - lat1); const dLon = toRadians(lon2 - lon1);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) ** 2;
  return earthRadius * (2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h)));
};

const formatDistanceLabel = (distanceMeters) => {
  const distance = Number(distanceMeters);
  if (!Number.isFinite(distance)) return '-';
  if (distance >= 1000) return `${(distance / 1000).toFixed(2)} km`;
  return `${Math.round(distance)} m`;
};

const normalizePoint = (source = {}) => {
  const latitudeRaw = source?.latitude ?? source?.lat ?? source?.coord_lat ?? source?.location_lat;
  const longitudeRaw = source?.longitude ?? source?.lng ?? source?.lon ?? source?.coord_lng ?? source?.location_lng;
  return { latitude: Number(latitudeRaw), longitude: Number(longitudeRaw) };
};

export default function TambahPelangganDrawer({ open, onClose, onSubmit, customer = null, packageOptions = [], sites = [], nasRouters = [], olts = [], odps = [] }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(INIT);
  const [errors, setErrors] = useState({});
  const [autoAssignInfo, setAutoAssignInfo] = useState('');
  const [geoError, setGeoError] = useState('');
  const [isLocating, setIsLocating] = useState(false);
  const [isMapPickerOpen, setIsMapPickerOpen] = useState(false);
  const isEditMode = Boolean(customer?.id);

  useEffect(() => {
    if (!open) return;
    setStep(1); setErrors({}); setAutoAssignInfo(''); setGeoError(''); setIsLocating(false); setIsMapPickerOpen(false);
    if (isEditMode) {
      setForm({
        ...INIT,
        customerName: String(customer?.name || ''), phone: String(customer?.phone || ''), email: String(customer?.email || ''),
        address: String(customer?.address || ''), packageId: String(customer?.package || ''), notes: String(customer?.notes || ''),
        accessType: String(customer?.accessType || customer?.network_access_type || customer?.access_type || customer?.networkAccessType || 'FTTH_PPPOE').trim().toUpperCase() === 'HTB_PPPOE' ? 'HTB_PPPOE' : 'FTTH_PPPOE',
        pppoeUsername: String(customer?.pppoe_username || ''), pppoePassword: String(customer?.pppoe_password || ''), portalPassword: '',
        latitude: String(customer?.latitude || ''), longitude: String(customer?.longitude || ''),
        siteId: String(customer?.site_id || ''), nasId: String(customer?.nas_id || customer?.router_id || ''),
        oltId: String(customer?.olt_id || ''), odpId: String(customer?.odp_id || ''), ponPort: String(customer?.pon_port || ''),
        onuSerial: String(customer?.onu_serial || ''), genieacsDeviceId: String(customer?.genieacs_device_id || ''),
      });
    } else { setForm(INIT); }
  }, [open, isEditMode, customer]);

  const isFtth = String(form.accessType || '').toUpperCase() !== 'HTB_PPPOE';
  const setField = (k, v) => { setForm((p) => ({ ...p, [k]: v })); setErrors((p) => ({ ...p, [k]: '' })); };

  const pkg = useMemo(() => packageOptions.find((x) => id(x?.id) === id(form.packageId)) || null, [packageOptions, form.packageId]);
  const siteList = useMemo(() => (sites || []).map((x) => {
    const point = normalizePoint(x);
    return { id: id(x?.id), name: String(x?.name || x?.code || x?.id || '-'), latitude: Number.isFinite(point.latitude) ? point.latitude : null, longitude: Number.isFinite(point.longitude) ? point.longitude : null };
  }).filter((x) => x.id), [sites]);
  const nasList = useMemo(() => (nasRouters || []).map((x) => ({ id: id(x?.router_id || x?.id), name: String(x?.name || x?.nas_identifier || x?.id || '-'), siteId: id(x?.site_id) })).filter((x) => x.id), [nasRouters]);
  const oltList = useMemo(() => (olts || []).map((x) => ({ id: id(x?.id), name: String(x?.name || x?.code || x?.id || '-'), routerId: id(x?.router_id) })).filter((x) => x.id), [olts]);
  const odpList = useMemo(() => (odps || []).map((x) => {
    const cap = Number(x?.capacity ?? x?.odp_capacity ?? x?.suggested_capacity ?? 8);
    const used = Number(x?.customer_count ?? x?.odp_customer_count ?? 0);
    const point = normalizePoint(x);
    return { id: id(x?.id), name: String(x?.name || x?.code || x?.id || '-'), oltId: id(x?.olt_id), routerId: id(x?.router_id), ponPort: String(x?.pon_port || x?.interface_name || '').trim(), capLeft: Math.max(0, (Number.isFinite(cap) ? cap : 8) - (Number.isFinite(used) ? used : 0)), latitude: Number.isFinite(point.latitude) ? point.latitude : null, longitude: Number.isFinite(point.longitude) ? point.longitude : null };
  }).filter((x) => x.id), [odps]);
  const selectedSite = siteList.find((x) => x.id === id(form.siteId));
  const nasOptions = nasList.filter((x) => !id(form.siteId) || !x.siteId || x.siteId === id(form.siteId));
  const selectedNas = nasOptions.find((x) => x.id === id(form.nasId));
  const oltOptions = oltList.filter((x) => !id(form.nasId) || !x.routerId || x.routerId === id(form.nasId));
  const selectedOlt = oltOptions.find((x) => x.id === id(form.oltId));
  const odpOptions = odpList.filter((x) => !id(form.oltId) || x.oltId === id(form.oltId));
  const selectedOdp = odpOptions.find((x) => x.id === id(form.odpId));

  const hasLocation = hasValidCoordinates(form.latitude, form.longitude);
  const mapFallbackCenter = useMemo(() => (hasLocation ? { latitude: Number(form.latitude), longitude: Number(form.longitude) } : DEFAULT_CUSTOMER_MAP_CENTER), [form.latitude, form.longitude, hasLocation]);

  const nearestSiteContext = useMemo(() => {
    if (!hasLocation) return null;
    const customerLat = Number(form.latitude); const customerLon = Number(form.longitude);
    let nearest = null;
    siteList.forEach((site) => {
      if (!hasValidCoordinates(site.latitude, site.longitude)) return;
      const distance = computeDistanceMeters(customerLat, customerLon, site.latitude, site.longitude);
      if (!Number.isFinite(distance)) return;
      if (!nearest || distance < nearest.distanceMeters) nearest = { ...site, distanceMeters: distance };
    });
    return nearest;
  }, [hasLocation, form.latitude, form.longitude, siteList]);

  const nearestOdpContext = useMemo(() => {
    if (!hasLocation) return null;
    const customerLat = Number(form.latitude); const customerLon = Number(form.longitude);
    let nearest = null;
    odpList.forEach((odp) => {
      if (!hasValidCoordinates(odp.latitude, odp.longitude)) return;
      const distance = computeDistanceMeters(customerLat, customerLon, odp.latitude, odp.longitude);
      if (!Number.isFinite(distance)) return;
      if (!nearest || distance < nearest.distanceMeters) nearest = { ...odp, distanceMeters: distance };
    });
    return nearest;
  }, [hasLocation, form.latitude, form.longitude, odpList]);

  const validate = (n) => {
    const e = {};
    if (n === 1) {
      if (!id(form.customerName)) e.customerName = 'Nama wajib diisi';
      if (!id(form.phone)) e.phone = 'Nomor HP wajib diisi';
      if (!id(form.address)) e.address = 'Alamat wajib diisi';
      if (!id(form.packageId)) e.packageId = 'Paket wajib dipilih';
      if (!id(form.pppoeUsername)) e.pppoeUsername = 'Username PPPoE wajib diisi';
      if (!id(form.pppoePassword) || id(form.pppoePassword).length < 6) e.pppoePassword = 'Password PPPoE minimal 6 karakter';
      if (!isEditMode && (!id(form.portalPassword) || id(form.portalPassword).length < 6)) e.portalPassword = 'Password portal minimal 6 karakter';
      if (isEditMode && id(form.portalPassword) && id(form.portalPassword).length < 6) e.portalPassword = 'Password portal minimal 6 karakter';
      const hasLat = id(form.latitude); const hasLon = id(form.longitude);
      if ((hasLat && !hasLon) || (!hasLat && hasLon)) { e.latitude = 'Latitude dan longitude harus diisi berpasangan'; e.longitude = 'Latitude dan longitude harus diisi berpasangan'; }
      if (hasLat && hasLon && !hasValidCoordinates(form.latitude, form.longitude)) { e.latitude = 'Koordinat tidak valid'; e.longitude = 'Koordinat tidak valid'; }
    }
    if (n === 2) {
      if (!id(form.siteId)) e.siteId = 'Site wajib dipilih';
      if (!id(form.nasId)) e.nasId = 'NAS wajib dipilih';
      if (isFtth) {
        if (!id(form.oltId)) e.oltId = 'OLT wajib dipilih';
        if (!id(form.odpId)) e.odpId = 'ODP wajib dipilih';
        if (!id(form.ponPort)) e.ponPort = 'PON Port wajib diisi';
        if (!id(form.onuSerial) && !id(form.genieacsDeviceId)) e.onuSerial = 'Isi ONU Serial atau GenieACS Device ID';
        if (selectedOdp && selectedOdp.capLeft <= 0) e.odpId = 'ODP penuh, pilih ODP lain';
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onAutoAssign = () => {
    const cands = odpList.filter((x) => x.capLeft > 0).sort((a, b) => {
      if (hasLocation) {
        const distA = hasValidCoordinates(a.latitude, a.longitude) ? computeDistanceMeters(Number(form.latitude), Number(form.longitude), a.latitude, a.longitude) : Number.POSITIVE_INFINITY;
        const distB = hasValidCoordinates(b.latitude, b.longitude) ? computeDistanceMeters(Number(form.latitude), Number(form.longitude), b.latitude, b.longitude) : Number.POSITIVE_INFINITY;
        if (distA !== distB) return distA - distB;
      }
      return b.capLeft - a.capLeft;
    });
    if (!cands.length) { setAutoAssignInfo('Rekomendasi tidak ditemukan, lanjutkan pemilihan manual.'); return; }
    const top = cands[0];
    const olt = oltList.find((x) => x.id === top.oltId);
    const nas = nasList.find((x) => x.id === id(top.routerId || olt?.routerId));
    const distanceText = hasLocation && hasValidCoordinates(top.latitude, top.longitude) ? ` (jarak ${formatDistanceLabel(computeDistanceMeters(Number(form.latitude), Number(form.longitude), top.latitude, top.longitude))})` : '';
    setForm((p) => ({ ...p, siteId: id(nas?.siteId || p.siteId), nasId: id(nas?.id || p.nasId), oltId: id(olt?.id || p.oltId), odpId: id(top.id), ponPort: id(top.ponPort || p.ponPort) }));
    setAutoAssignInfo(`Rekomendasi: ${nas?.name || '-'} -> ${olt?.name || '-'} -> ${top.name}${distanceText}`);
  };

  const genCred = () => {
    const base = id(form.customerName).toLowerCase().replace(/[^a-z0-9]+/g, '');
    const suffix = String(Date.now()).slice(-4);
    const user = base ? `${base}-${suffix}` : `user-${suffix}`;
    const pass = `ktc${suffix}#`;
    setForm((x) => ({ ...x, pppoeUsername: x.pppoeUsername || user, pppoePassword: x.pppoePassword || pass, portalPassword: x.portalPassword || pass }));
  };

  const handleGetCurrentLocation = () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) { setGeoError('Browser tidak mendukung geolocation. Isi koordinat manual atau pilih di peta.'); return; }
    setGeoError(''); setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => { setIsLocating(false); setField('latitude', Number(position.coords.latitude).toFixed(6)); setField('longitude', Number(position.coords.longitude).toFixed(6)); },
      (error) => {
        setIsLocating(false);
        if (error?.code === 1) setGeoError('Akses lokasi ditolak. Aktifkan izin lokasi lalu coba lagi.');
        else if (error?.code === 2) setGeoError('Lokasi tidak tersedia. Pastikan GPS/jaringan aktif.');
        else if (error?.code === 3) setGeoError('Pengambilan lokasi timeout. Coba lagi atau isi manual.');
        else setGeoError('Gagal mengambil lokasi saat ini. Gunakan input manual atau pilih di peta.');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleMapLocationChange = (latitude, longitude) => {
    setField('latitude', Number(latitude).toFixed(6));
    setField('longitude', Number(longitude).toFixed(6));
    setGeoError('');
  };

  const next = () => { if (!validate(step)) return; setStep((s) => Math.min(3, s + 1)); };
  const submit = () => {
    const ok1 = validate(1); const ok2 = validate(2);
    if (!ok1 || !ok2) { setStep(ok1 ? 2 : 1); return; }
    onSubmit?.({
      ...form,
      customerId: isEditMode ? customer?.id : null,
      packageName: pkg?.name || '',
      packagePrice: Number(pkg?.price || pkg?.monthly || 0),
      infraSummary: { siteName: selectedSite?.name || '', nasName: selectedNas?.name || '', oltName: selectedOlt?.name || '', odpName: selectedOdp?.name || '', odpCapLeft: Number(selectedOdp?.capLeft || 0) },
      locationSummary: { latitude: id(form.latitude), longitude: id(form.longitude) },
    });
  };

  if (!open) return null;
  const checklistItems = [
    { key: 'site', label: 'Site', state: id(form.siteId) ? 'ok' : 'pending' },
    { key: 'nas', label: 'NAS', state: id(form.nasId) ? 'ok' : 'pending' },
    { key: 'olt', label: 'OLT', state: !isFtth || id(form.oltId) ? 'ok' : 'pending' },
    { key: 'odp', label: 'ODP', state: !isFtth || id(form.odpId) ? 'ok' : 'pending' },
    { key: 'cap', label: 'Kapasitas', state: !isFtth || !selectedOdp || selectedOdp.capLeft > 0 ? 'ok' : 'warn', text: !isFtth || !selectedOdp || selectedOdp.capLeft > 0 ? 'Tersedia' : 'Penuh' },
  ];

  const step1Ready = Boolean(id(form.customerName) && id(form.phone) && id(form.address) && id(form.packageId) && id(form.pppoeUsername) && id(form.pppoePassword) && (isEditMode ? true : id(form.portalPassword)));
  const step2Ready = Boolean(id(form.siteId) && id(form.nasId) && (!isFtth || (id(form.oltId) && id(form.odpId) && id(form.ponPort))) && (!isFtth || !selectedOdp || selectedOdp.capLeft > 0));
  const primaryDisabled = step < 3 ? !(step === 1 ? step1Ready : step2Ready) : !(step1Ready && step2Ready);

  const mapReferenceLabel = selectedOdp?.name || nearestOdpContext?.name || selectedSite?.name || nearestSiteContext?.name || '-';
  const mapReferenceSummary = selectedOdp?.name
    ? `ODP terpilih ${selectedOdp.name}${hasValidCoordinates(selectedOdp?.latitude, selectedOdp?.longitude) && hasLocation ? ` � ${formatDistanceLabel(computeDistanceMeters(Number(form.latitude), Number(form.longitude), selectedOdp.latitude, selectedOdp.longitude))}` : ''}`
    : (nearestOdpContext ? `ODP terdekat ${nearestOdpContext.name} � ${formatDistanceLabel(nearestOdpContext.distanceMeters)}` : (nearestSiteContext ? `Site terdekat ${nearestSiteContext.name} � ${formatDistanceLabel(nearestSiteContext.distanceMeters)}` : 'Belum ada referensi lokasi'));

  return (
    <div className="fixed inset-0 z-[120] font-['Inter']">
      <button type="button" className="absolute inset-0 h-full w-full bg-black/65 backdrop-blur-sm" aria-label="Tutup tambah pelanggan" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-[78vw] max-w-[1200px] bg-[linear-gradient(160deg,rgba(15,24,38,0.82)_0%,rgba(10,17,28,0.94)_100%)] backdrop-blur-xl">
        <div className="flex h-full flex-col">
          <header className="sticky top-0 z-20 bg-[rgba(10,17,28,0.82)] px-6 py-4 backdrop-blur-xl">
            <div className="mb-3 flex items-start justify-between gap-4">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#6f829d]">{isEditMode ? 'Edit Pelanggan' : 'Tambah Pelanggan Baru'}</p>
                <h2 className="mt-1 text-2xl font-semibold tracking-tight text-[#e7f0fa]">{STEPS[step - 1].title}</h2>
                <p className="text-sm text-[#8ea3bd]">{STEPS[step - 1].desc}</p>
                <p className="mt-1 text-[11px] uppercase tracking-[0.08em] text-[#7f93ac]">Langkah {step} dari 3</p>
              </div>
              <UIButton type="button" variant="ghost" onClick={onClose}>Tutup</UIButton>
            </div>
            <Stepper steps={STEPS} currentStep={step} />
          </header>

          <section className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            {step === 1 && (
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.25fr_0.75fr]">
                <SectionContainer>
                  <FormSection title="Data Utama" description="Isi identitas pelanggan dan paket layanan.">
                    <div className="grid gap-3 md:grid-cols-2">
                      <FormField label="Nama Pelanggan" required error={errors.customerName} className="md:col-span-2"><Input value={form.customerName} onChange={(e) => setField('customerName', e.target.value)} className={inputClassName} /></FormField>
                      <FormField label="Nomor HP" required error={errors.phone}><Input value={form.phone} onChange={(e) => setField('phone', e.target.value)} className={inputClassName} /></FormField>
                      <FormField label="Email"><Input value={form.email} onChange={(e) => setField('email', e.target.value)} className={inputClassName} /></FormField>
                      <FormField label="Paket" required error={errors.packageId}>
                        <Select value={form.packageId} onValueChange={(v) => setField('packageId', v)}>
                          <SelectTrigger className={inputClassName}><SelectValue placeholder="Pilih paket" /></SelectTrigger>
                          <SelectContent className="z-[240] border-0 bg-[#121f31] text-[#d5e4f4]">{packageOptions.map((p) => <SelectItem key={id(p?.id)} value={id(p?.id)}>{String(p?.name || '-')}</SelectItem>)}</SelectContent>
                        </Select>
                      </FormField>
                      <FormField label="Estimasi Tagihan"><Input value={pkg ? money(pkg?.price || pkg?.monthly) : '-'} disabled className="border-0 bg-[rgba(10,17,28,0.55)] text-[#9ab0c8]" /></FormField>
                      <FormField label="Catatan" className="md:col-span-2"><Input value={form.notes} onChange={(e) => setField('notes', e.target.value)} className={inputClassName} /></FormField>
                    </div>
                  </FormSection>

                  <LocationFieldGroup
                    className="mt-4"
                    address={form.address} latitude={form.latitude} longitude={form.longitude}
                    addressError={errors.address} latitudeError={errors.latitude} longitudeError={errors.longitude}
                    onAddressChange={(value) => setField('address', value)}
                    onLatitudeChange={(value) => setField('latitude', value)}
                    onLongitudeChange={(value) => setField('longitude', value)}
                    onGetCurrentLocation={handleGetCurrentLocation}
                    onPickOnMap={() => setIsMapPickerOpen(true)}
                    isLocating={isLocating}
                    geoHint={geoError || 'Koordinat bisa diisi manual jika geolocation tidak tersedia.'}
                  />
                </SectionContainer>

                <SectionContainer>
                  <FormSection title="Credential PPPoE" description="Generate otomatis atau isi manual untuk akses pelanggan." action={(<UIButton type="button" variant="subtle" onClick={genCred}><Wand2 className="h-3.5 w-3.5" />Generate</UIButton>)}>
                    <div className="space-y-2">
                      <FormField label="Username PPPoE" required error={errors.pppoeUsername}><Input value={form.pppoeUsername} onChange={(e) => setField('pppoeUsername', e.target.value)} className={`${inputClassName} font-mono`} /></FormField>
                      <FormField label="Password PPPoE" required error={errors.pppoePassword}><Input value={form.pppoePassword} onChange={(e) => setField('pppoePassword', e.target.value)} className={`${inputClassName} font-mono`} /></FormField>
                      <FormField label={`Password Portal ${isEditMode ? '(opsional)' : ''}`} required={!isEditMode} error={errors.portalPassword}><Input value={form.portalPassword} onChange={(e) => setField('portalPassword', e.target.value)} className={inputClassName} /></FormField>
                    </div>
                  </FormSection>
                </SectionContainer>
              </div>
            )}

            {step === 2 && (
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.15fr_0.85fr]">
                <SectionContainer>
                  <FormSection title="Mapping Infrastruktur" description="Ikuti urutan Site -> NAS -> OLT -> ODP untuk menghindari mismatch.">
                    <div className="grid gap-3 md:grid-cols-2">
                      <FormField label="Site" required helper="Pilih site untuk melihat NAS yang tersedia" error={errors.siteId}><Select value={form.siteId} onValueChange={(v) => { setField('siteId', v); setForm((p) => ({ ...p, nasId: '', oltId: '', odpId: '', ponPort: '' })); }}><SelectTrigger className={inputClassName}><SelectValue placeholder="Pilih site" /></SelectTrigger><SelectContent className="z-[240] border-0 bg-[#121f31] text-[#d5e4f4]">{siteList.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select></FormField>
                      <FormField label="NAS" required helper={!id(form.siteId) ? 'Pilih site terlebih dahulu' : 'NAS aktif pada site terpilih'} error={errors.nasId}><Select value={form.nasId} onValueChange={(v) => { setField('nasId', v); setForm((p) => ({ ...p, oltId: '', odpId: '', ponPort: '' })); }} disabled={!id(form.siteId)}><SelectTrigger className={inputClassName}><SelectValue placeholder="Pilih NAS" /></SelectTrigger><SelectContent className="z-[240] border-0 bg-[#121f31] text-[#d5e4f4]">{nasOptions.map((n) => <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>)}</SelectContent></Select></FormField>
                      <FormField label="OLT" required={isFtth} helper={!id(form.nasId) ? 'Pilih NAS terlebih dahulu' : 'OLT terhubung ke NAS'} error={errors.oltId}><Select value={form.oltId} onValueChange={(v) => { setField('oltId', v); setForm((p) => ({ ...p, odpId: '', ponPort: '' })); }} disabled={!id(form.nasId)}><SelectTrigger className={inputClassName}><SelectValue placeholder="Pilih OLT" /></SelectTrigger><SelectContent className="z-[240] border-0 bg-[#121f31] text-[#d5e4f4]">{oltOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select></FormField>
                      <FormField label="ODP" required={isFtth} helper={!id(form.oltId) ? 'Pilih OLT terlebih dahulu' : 'Pilih ODP dengan kapasitas tersedia'} error={errors.odpId}><Select value={form.odpId} onValueChange={(v) => { setField('odpId', v); const o = odpOptions.find((x) => x.id === v); if (o?.ponPort) setField('ponPort', o.ponPort); }} disabled={!id(form.oltId)}><SelectTrigger className={inputClassName}><SelectValue placeholder="Pilih ODP" /></SelectTrigger><SelectContent className="z-[240] border-0 bg-[#121f31] text-[#d5e4f4]">{odpOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.name} ({o.capLeft} slot)</SelectItem>)}</SelectContent></Select></FormField>
                      {isFtth && <FormField label="PON Port" required error={errors.ponPort} className="md:col-span-2"><Input value={form.ponPort} onChange={(e) => setField('ponPort', e.target.value)} className={inputClassName} /></FormField>}
                      {isFtth && <FormField label="ONU Serial / GenieACS" required helper="Isi salah satu jika FTTH" error={errors.onuSerial} className="md:col-span-2"><div className="grid gap-2 md:grid-cols-2"><Input value={form.onuSerial} onChange={(e) => setField('onuSerial', e.target.value)} placeholder="ONU Serial" className={inputClassName} /><Input value={form.genieacsDeviceId} onChange={(e) => setField('genieacsDeviceId', e.target.value)} placeholder="GenieACS Device ID" className={inputClassName} /></div></FormField>}
                    </div>
                  </FormSection>

                  <FormSection className="mt-4" title="Auto Assign" description="Sistem merekomendasikan NAS/ODP berdasarkan site, lokasi, dan slot yang tersedia." action={(<UIButton type="button" onClick={onAutoAssign}><Sparkles className="h-4 w-4" />Auto Assign</UIButton>)}>
                    {autoAssignInfo ? <p className="rounded-lg bg-[rgba(10,17,28,0.62)] px-3 py-2 text-xs text-[#d5e4f4]">{autoAssignInfo}</p> : <p className="text-xs text-[#8ea3bd]">Rekomendasi akan memperhitungkan jarak lokasi pelanggan jika koordinat tersedia.</p>}
                  </FormSection>
                </SectionContainer>

                <SectionContainer>
                  <FormSection title="Checklist Infrastruktur" description="Status validasi inti untuk proses provisioning."><InlineStatusRow items={checklistItems} /></FormSection>
                  <FormSection className="mt-4" title="Konteks Lokasi" description="Ringkasan lokasi pelanggan untuk membantu keputusan assignment.">
                    <div className="space-y-2 text-xs text-[#8ea3bd]">
                      <p>Titik pelanggan: <span className="font-mono text-[#d5e4f4]">{hasLocation ? `${Number(form.latitude).toFixed(6)}, ${Number(form.longitude).toFixed(6)}` : 'Belum tersedia'}</span></p>
                      <p>Site terdekat: <span className="text-[#d5e4f4]">{nearestSiteContext ? `${nearestSiteContext.name} (${formatDistanceLabel(nearestSiteContext.distanceMeters)})` : '-'}</span></p>
                      <p>ODP terdekat: <span className="text-[#d5e4f4]">{nearestOdpContext ? `${nearestOdpContext.name} (${formatDistanceLabel(nearestOdpContext.distanceMeters)})` : '-'}</span></p>
                    </div>
                  </FormSection>
                </SectionContainer>
              </div>
            )}

            {step === 3 && (
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <ReviewCard title="Ringkasan Pelanggan" rows={[{ label: 'Nama', value: form.customerName }, { label: 'No HP', value: form.phone }, { label: 'Email', value: form.email }, { label: 'Alamat', value: form.address }, { label: 'Paket', value: pkg?.name }]} />
                <ReviewCard title="Ringkasan Infrastruktur" rows={[{ label: 'Site', value: selectedSite?.name }, { label: 'NAS', value: selectedNas?.name }, { label: 'OLT', value: selectedOlt?.name }, { label: 'ODP', value: selectedOdp?.name }, { label: 'Sisa ODP', value: selectedOdp ? `${selectedOdp.capLeft} slot` : '-' }]} />
                <MapReviewCard className="xl:col-span-2" latitude={form.latitude} longitude={form.longitude} referenceLabel={mapReferenceLabel} referenceSummary={mapReferenceSummary} />
              </div>
            )}
          </section>

          <StickyActionBar
            left={<UIButton type="button" variant="ghost" onClick={onClose}>Batal</UIButton>}
            right={<>{step > 1 ? <UIButton type="button" variant="ghost" onClick={() => setStep((s) => Math.max(1, s - 1))}><ChevronLeft className="h-4 w-4" />Kembali</UIButton> : null}{step < 3 ? <UIButton type="button" disabled={primaryDisabled} onClick={next}>Lanjut <ChevronRight className="h-4 w-4" /></UIButton> : <UIButton type="button" disabled={primaryDisabled} onClick={submit}>{isEditMode ? 'Perbarui Pelanggan' : 'Simpan Pelanggan'}</UIButton>}</>}
          />
        </div>
      </aside>

      <Dialog open={isMapPickerOpen} onOpenChange={setIsMapPickerOpen}>
        <DialogContent className="max-w-3xl border-0 bg-[linear-gradient(160deg,rgba(15,24,38,0.96)_0%,rgba(10,17,28,0.98)_100%)] text-[#d5e4f4]">
          <DialogHeader><DialogTitle className="text-[#e7f0fa]">Pilih Titik Instalasi di Peta</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <LeafletPinPicker title="Klik peta atau geser pin untuk menentukan titik instalasi" latitude={form.latitude} longitude={form.longitude} fallbackCenter={mapFallbackCenter} onChange={handleMapLocationChange} heightClass="h-72" />
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-[#8ea3bd]">Titik aktif: <span className="font-mono text-[#d5e4f4]">{hasLocation ? `${Number(form.latitude).toFixed(6)}, ${Number(form.longitude).toFixed(6)}` : 'belum dipilih'}</span></p>
              <UIButton type="button" onClick={() => setIsMapPickerOpen(false)}><MapPin className="h-4 w-4" />Simpan Titik</UIButton>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
